// ============================================
// DEMOGRAPHIC COMPARISON VIEW - Grouped Bar Chart
// Shows segment averages side-by-side across all muscles
// ============================================

import { useMemo } from 'react';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
    ResponsiveContainer
} from 'recharts';
import { getHebrewName } from '../../constants/muscles';
import { DEMOGRAPHIC_LABELS } from '../../constants/demographics';
import type { DemographicDisplaySegment } from '../../services/demographics';
import CustomizedAxisTick from './CustomizedAxisTick';

interface DemographicComparisonViewProps {
    segments: DemographicDisplaySegment[];
    secondarySegments?: DemographicDisplaySegment[] | null;
}

export default function DemographicComparisonView({ segments, secondarySegments }: DemographicComparisonViewProps) {
    const displaySegments = secondarySegments && secondarySegments.length > 0
        ? secondarySegments
        : segments;

    const chartData = useMemo(() => {
        if (displaySegments.length === 0) return [];

        // Get all categories from the first segment
        const categories = Object.keys(displaySegments[0].averages);

        return categories.map(category => {
            const entry: Record<string, any> = {
                name: getHebrewName(category),
            };
            displaySegments.forEach(seg => {
                entry[seg.key] = parseFloat((seg.averages[category] ?? 0).toFixed(2));
            });
            return entry;
        });
    }, [displaySegments]);

    if (displaySegments.length < 2) {
        return (
            <div className="analytics-demographic-empty" style={{
                textAlign: 'center',
                padding: '40px 20px',
                color: 'var(--neutral-500)',
                fontSize: '14px'
            }}>
                <p>{DEMOGRAPHIC_LABELS.tooFewRespondents}</p>
                <p style={{ fontSize: '12px', marginTop: '8px', color: 'var(--neutral-400)' }}>
                    נדרשות לפחות 2 קבוצות עם 3+ משתתפים כל אחת
                </p>
            </div>
        );
    }

    return (
        <div className="analytics-demographic-comparison" style={{ direction: 'ltr', width: '100%', height: '100%', minHeight: '380px' }}>
            <ResponsiveContainer width="100%" height="100%">
                <BarChart
                    data={chartData}
                    margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis
                        dataKey="name"
                        interval={0}
                        tick={<CustomizedAxisTick />}
                        height={80}
                    />
                    <YAxis
                        domain={[0, 10]}
                        ticks={[0, 2, 4, 6, 8, 10]}
                        tick={{ fontSize: 11 }}
                    />
                    <Tooltip
                        cursor={{ fill: '#f4f1eb' }}
                        contentStyle={{
                            backgroundColor: '#ffffff',
                            borderRadius: '12px',
                            border: '1px solid #ebe6de',
                            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                            direction: 'rtl',
                            textAlign: 'right',
                            padding: '12px'
                        }}
                        formatter={(value: any, name?: string) => [
                            typeof value === 'number' ? value.toFixed(2) : value,
                            name || ''
                        ]}
                    />
                    <Legend
                        content={() => (
                            <div style={{
                                display: 'flex',
                                justifyContent: 'center',
                                gap: '20px',
                                direction: 'rtl',
                                paddingTop: '20px',
                                flexWrap: 'wrap'
                            }}>
                                {displaySegments.map(seg => (
                                    <div key={seg.key} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <span style={{
                                            width: '12px',
                                            height: '12px',
                                            borderRadius: '3px',
                                            backgroundColor: seg.color,
                                            display: 'inline-block',
                                            flexShrink: 0
                                        }} />
                                        <span style={{ fontSize: '12px', color: '#5c5549', whiteSpace: 'nowrap' }}>
                                            {seg.labelHe} (N={seg.count})
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    />

                    {displaySegments.map(seg => (
                        <Bar
                            key={seg.key}
                            name={seg.key}
                            dataKey={seg.key}
                            fill={seg.color}
                            radius={[4, 4, 0, 0]}
                        />
                    ))}
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
}
